import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface Overview {
  tenants: { total: number; active: number; internal: number; quarantined: number }
  users: { total: number; new24h: number }
  sites: { total: number; verified: number }
  runs: { total: number; last24h: number; runningNow: number }
  fixes: { total: number; applied: number }
  spend: { monthToDateCents: number; scansThisMonth: number }
}

export function AdminOverviewPage() {
  const t = useT()
  const overview = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => apiClient.get<Overview>('/admin/overview'),
    refetchInterval: 30_000,
  })

  if (overview.isLoading) return <p>{t('common.loading')}</p>
  if (overview.isError || !overview.data) return <p>{t('common.error')}</p>

  const d = overview.data
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">{t('admin.overview.title')}</h1>

      <Section title={t('admin.overview.tenants')}>
        <Stat label={t('admin.overview.total')} value={d.tenants.total} />
        <Stat label={t('admin.overview.active')} value={d.tenants.active} />
        <Stat label={t('admin.overview.internal')} value={d.tenants.internal} />
        <Stat label={t('admin.overview.quarantined')} value={d.tenants.quarantined} tone={d.tenants.quarantined > 0 ? 'warn' : undefined} />
      </Section>

      <Section title={t('admin.overview.users')}>
        <Stat label={t('admin.overview.total')} value={d.users.total} />
        <Stat label={t('admin.overview.new24h')} value={d.users.new24h} />
      </Section>

      <Section title={t('admin.overview.sites')}>
        <Stat label={t('admin.overview.total')} value={d.sites.total} />
        <Stat label={t('admin.overview.verified')} value={d.sites.verified} />
      </Section>

      <Section title={t('admin.overview.runs')}>
        <Stat label={t('admin.overview.total')} value={d.runs.total} />
        <Stat label={t('admin.overview.last24h')} value={d.runs.last24h} />
        <Stat label={t('admin.overview.runningnow')} value={d.runs.runningNow} tone={d.runs.runningNow > 0 ? 'live' : undefined} />
      </Section>

      <Section title={t('admin.overview.fixes')}>
        <Stat label={t('admin.overview.total')} value={d.fixes.total} />
        <Stat label={t('admin.overview.applied')} value={d.fixes.applied} />
      </Section>

      <Section title={t('admin.overview.spend')}>
        <Stat label={t('admin.overview.month_to_date')} value={`$${(d.spend.monthToDateCents / 100).toFixed(2)}`} />
        <Stat label={t('admin.overview.scans_month')} value={d.spend.scansThisMonth} />
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium opacity-80 mb-2">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    </section>
  )
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'warn' | 'live' }) {
  const color = tone === 'warn' ? 'var(--color-warn)' : tone === 'live' ? 'var(--color-accent)' : 'inherit'
  return (
    <div className="p-3 rounded border" style={{ borderColor: 'var(--color-border)' }}>
      <div className="text-xs opacity-70">{label}</div>
      <div className="text-xl font-semibold mt-0.5" style={{ color }}>{value}</div>
    </div>
  )
}
